import * as Dig from "../game/dig.ts";
import * as Routine from "./routine.ts";
import * as Sprites from "./sprites.ts";

export type Size = { readonly width: number; readonly height: number };

export type Op =
  | { readonly kind: "clear"; readonly color: string }
  | {
      readonly kind: "rect";
      readonly x: number;
      readonly y: number;
      readonly width: number;
      readonly height: number;
      readonly color: string;
    }
  | {
      readonly kind: "sprite";
      readonly sprite: Sprites.Sprite;
      readonly x: number;
      readonly y: number;
      readonly scale: number;
      readonly ghost?: boolean;
    };

const SKY = "#5aa9e6";

export const SPARK = "#ffffff";
const RAY = "#f9d94a";
const SPARK_MS = 120;
export const DEBRIS_MS = 450;
const BITS = 3;
const GRAVITY = 300;
const SINK = 10;

const flips = new WeakMap<Sprites.Sprite, Sprites.Sprite>();

const flipped = (sprite: Sprites.Sprite): Sprites.Sprite => {
  const known = flips.get(sprite);
  if (known !== undefined) {
    return known;
  }
  const next = {
    rows: sprite.rows.map((row) => row.split("").reverse().join("")),
    palette: sprite.palette,
  };
  flips.set(sprite, next);
  return next;
};

const landing = (
  direction: Dig.Direction,
): { readonly x: number; readonly y: number; readonly out: number } => {
  switch (direction) {
    case "down":
      return { x: 0.5, y: 0, out: 0 };
    case "left":
      return { x: 1, y: 0.5, out: 1 };
    case "right":
      return { x: 0, y: 0.5, out: -1 };
    default:
      return direction satisfies never;
  }
};

const frameOf = (character: Sprites.Character, pose: Routine.Pose): Sprites.Sprite => {
  const frames = Sprites.DIGGERS[character];
  switch (pose.action) {
    case "wait":
      return frames.wait[pose.frame];
    case "ready":
      return frames.ready[pose.frame];
    case "walk":
      return pose.direction === "left" ? flipped(frames.walk[pose.frame]) : frames.walk[pose.frame];
    case "dig":
      switch (pose.direction) {
        case "down":
          return frames.digDown[pose.frame];
        case "left":
          return frames.digLeft[pose.frame];
        case "right":
          return frames.digRight[pose.frame];
        default:
          return pose.direction satisfies never;
      }
    default:
      return pose satisfies never;
  }
};

// Keyed on the world square, not the screen one, so the texture stays put as the camera moves.
const tileSprite = (kind: string, column: number, row: number): Sprites.Sprite | undefined => {
  const sheet = Sprites.TILES[kind];
  if (sheet === undefined) {
    return undefined;
  }
  const count = sheet.variants.length;
  return sheet.variants[(((column + row) % count) + count) % count];
};

const block = (
  scene: Routine.Scene,
  column: number,
  row: number,
): ReadonlyArray<Sprites.Sprite> => {
  const cell = scene.grid[row][column];
  const world = scene.originRow + row;
  if (!("health" in cell)) {
    return world < Dig.SURFACE ? [] : [Sprites.CAVE];
  }
  const whole = tileSprite(cell.kind, scene.originColumn + column, world);
  if (whole === undefined) {
    return [];
  }
  if (cell.health >= cell.maxHealth) {
    return [whole];
  }
  return [whole, Sprites.CRACKS[Sprites.crackStage(cell.health, cell.maxHealth)]];
};

export const ops = (size: Size, scene: Routine.Scene): ReadonlyArray<Op> => {
  const stage = {
    width: Routine.COLUMNS * Sprites.SIZE,
    height: Routine.VIEW_ROWS * Sprites.SIZE,
  };
  const scale = Math.max(
    1,
    Math.floor(Math.min(size.width / stage.width, size.height / stage.height)),
  );
  const left = Math.floor((size.width - stage.width * scale) / 2);
  const top = Math.floor((size.height - stage.height * scale) / 2);
  const at = (column: number, row: number): { readonly x: number; readonly y: number } => ({
    x: left + (column - scene.originColumn) * Sprites.SIZE * scale,
    y: top + (row - scene.camera) * Sprites.SIZE * scale,
  });
  const dot = (x: number, y: number, color: string): Op => ({
    kind: "rect",
    x,
    y,
    width: scale,
    height: scale,
    color,
  });

  const out: Array<Op> = [{ kind: "clear", color: SKY }];
  scene.grid.forEach((cells, row) => {
    cells.forEach((_, column) => {
      const world = scene.originRow + row;
      for (const sprite of block(scene, column, row)) {
        out.push({ kind: "sprite", sprite, ...at(scene.originColumn + column, world), scale });
      }
    });
  });
  for (const digger of scene.diggers) {
    if (digger.ghost && digger.target !== undefined) {
      out.push({
        kind: "sprite",
        sprite: Sprites.TARGET,
        ...at(digger.target.column, digger.target.row),
        scale,
        ghost: true,
      });
    }
  }
  for (const digger of scene.diggers) {
    const sink =
      digger.pose.action === "dig" && digger.pose.direction === "down" && digger.pose.frame >= 3
        ? SINK / Sprites.SIZE
        : 0;
    const character = Sprites.CHARACTERS[digger.slot % Sprites.CHARACTERS.length];
    const sprite = frameOf(character, digger.pose);
    const place = at(digger.column, digger.row + sink);
    if (digger.ghost) {
      out.push({ kind: "sprite", sprite: Sprites.ghost(sprite), ...place, scale, ghost: true });
      continue;
    }
    out.push({ kind: "sprite", sprite, ...place, scale });
  }
  for (const hit of scene.hits) {
    const age = (scene.now - hit.at) / 1_000;
    if (age < 0 || age >= DEBRIS_MS / 1_000) {
      continue;
    }
    const face = landing(hit.direction);
    const origin = at(hit.column + face.x, hit.row + face.y);
    if (age < SPARK_MS / 1_000) {
      out.push(dot(origin.x, origin.y, SPARK));
      const rays =
        face.out === 0
          ? [
              [-1, -1],
              [1, -1],
            ]
          : [
              [face.out, -1],
              [face.out, 1],
            ];
      for (const [dx, dy] of rays) {
        out.push(dot(origin.x + dx * scale, origin.y + dy * scale, RAY));
      }
    }
    for (let bit = 0; bit < BITS; bit++) {
      const sideways = (bit - (BITS - 1) / 2) * 30 + face.out * 60;
      const upward = 90 + bit * 14;
      out.push(
        dot(
          origin.x + sideways * age * scale,
          origin.y + (-upward * age + (GRAVITY / 2) * age * age) * scale,
          age < DEBRIS_MS / 2_000 ? Sprites.CAVE.palette.x : Sprites.CAVE.palette.U,
        ),
      );
    }
  }
  return out;
};
